import { Component, HostListener, OnInit } from '@angular/core';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { User } from 'firebase/auth';
import { FormsModule } from '@angular/forms';
import { CommonModule, NgIf } from '@angular/common';
import { getDownloadURL, getStorage, listAll, ref } from 'firebase/storage';
import { collection, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { Firestore } from '@angular/fire/firestore';
import { deleteDoc } from 'firebase/firestore';
import { PopupComponent } from '../../components/popup/popup.component';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notifications/notification.service';
import { environment } from '../../../environments/environment';
import { LanguageSelectorComponent } from '../../components/language-selector/language-selector.component';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [TranslatePipe, LanguageSelectorComponent, FormsModule, NgIf, CommonModule, PopupComponent, RouterLink, RouterLinkActive],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss'
})
export class TopbarComponent implements OnInit {
  user$!: Observable<User | null>;
  userId = '';

  notifications: any[] = [];
  rawNotifications: any[] = [];

  unreadCount = 0;

  showLogoutPopup = false;
  showNotifications = false;

  constructor(
    public router: Router,
    public authService: AuthService,
    private translate: TranslateService,
    private notificationService: NotificationService
  ) { }

  showProfileMenu = false;
  userProfileUrl: string | null = null;

  private firestore: Firestore = environment.firestore;
  private storage = getStorage();

  async ngOnInit() {
    this.user$ = this.authService.getUser();
    this.user$.subscribe(async user => {
      if (user) {
        this.userId = user.uid;
        this.userProfileUrl = await this.getUserProfileImage(user.uid);
        this.fetchNotifications();
      }
    });

    // fecha as notificações ao navegar
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.showNotifications = false;
      }
    });

    this.translate.onLangChange.subscribe(() => {
      this.buildNotifications();
    });
  }

  private unsubscribeNotifications?: () => void;

  // obter notificações
  fetchNotifications() {
    if (!this.userId) return;

    if (this.unsubscribeNotifications) {
      this.unsubscribeNotifications();
    }

    const notificationsRef = collection(
      this.firestore,
      'users',
      this.userId,
      'notifications'
    );

    this.unsubscribeNotifications = onSnapshot(
      notificationsRef,
      async (snapshot) => {

        this.rawNotifications = snapshot.docs.map(doc => ({
          notificationId: doc.id,
          ...doc.data()
        }));

        this.buildNotifications();

      }
    );
  }

  // cosntruir a notificação
  async buildNotifications() {
    const notifications = await Promise.all(
      this.rawNotifications.map(async (notification: any) => {
        const senderPhoto = notification.userSenderId
          ? await this.getUserProfileImage(notification.userSenderId)
          : null;

        const parts = notification.docPath?.split('/') || [];

        let message = '';

        if (notification.action === 'love') {
          message = notification.userSenderName + this.translate.instant('notification.love');
        }
        else if (notification.action === 'friend_request') {
          message = notification.userSenderName + this.translate.instant('notification.friend_request');
        }
        else if (notification.action === 'team_request') {
          message = this.translate.instant('notification.team_request');
        }
        else if (notification.action === 'event_invitation') {
          message = notification.userSenderName + this.translate.instant('notification.event_invitation') + await this.notificationService.fetchEventName(parts[1]);
        }
        else if (notification.action === 'team_invitation') {
          message = notification.userSenderName + this.translate.instant('notification.team_invitation') + await this.notificationService.fetchTeamName(parts[1]);
        }
        else if (notification.action === 'comment') {
          message = this.translate.instant('notification.comment') + notification.message;
        }
        else {
          message = notification.message;
        }

        return {
          ...notification,
          senderPhoto,
          message,
          id: parts[1],
          userChatId: parts[2],
        };
      })
    );

    notifications.sort(
      (a, b) => b.pubDate?.toDate() - a.pubDate?.toDate()
    );

    this.notifications = notifications;
    this.unreadCount = notifications.filter(n => !n.read).length;
  }


  // ao clciar nas notificações
  async onNotificationClick(notification: any) {
    if (!notification.read) {
      await this.updateNotificationReadState(notification.notificationId);
    }

    this.navigateTo(notification);
  }

  // atualizar o estado da notificação para lida
  async updateNotificationReadState(id: string) {
    const notification = doc(this.firestore, 'users', this.userId, 'notifications', id)

    const snap = await getDoc(notification);

    if (!snap.exists()) {
      return;
    }

    // atualizar o read para true
    await updateDoc(notification, { read: true });

    // atualizar localmente também
    const notifIndex = this.notifications.findIndex(n => n.notificationId === id);
    if (notifIndex !== -1) {
      this.notifications[notifIndex].read = true;
      this.unreadCount = this.notifications.filter(n => !n.read).length;
    }
  }

  // navega para a página relativa à notificação
  navigateTo(notification: any) {
    if (notification.action === 'team_request' || notification.action === 'team_invitation') this.router.navigate(['/team/', notification.id]);
    else if (notification.action === 'friend_request') this.router.navigate(['/profile/activities/', notification.userSenderId]);
    else if (notification.action === 'event_invitation') this.router.navigate(['/event/details/', notification.id]);
    else if (notification.action === 'comment') {
      this.router.navigate(['/post/', notification.id]);
      this.deleteAllNotifications(notification.id, 'comment', notification.userChatId);
    }
    else if (notification.action === 'love') {
      this.router.navigate(['/post/', notification.id]);
      this.deleteAllNotifications(notification.id, 'love');
    }
    else if (notification.action === 'event_chat') {
      this.router.navigate(['event/chat', notification.id]);
      this.deleteAllNotifications(notification.id, 'event_chat');
    }
    else if (notification.action === 'team_chat') {
      this.router.navigate(['team/chat', notification.id]);
      this.deleteAllNotifications(notification.id, 'team_chat');
    }
    else if (notification.action === 'user_chat') {
      this.router.navigate(['profile/friend/chat', notification.id, notification.userChatId]);
      this.deleteAllNotifications(notification.id, 'user_chat', notification.userChatId);
    }
  }

  // apagar um grupo de notificações
  // id -> é o id do post ou do chat
  // userChatId -> é o id do chat entre dois utilziadores
  async deleteAllNotifications(id: string, action: 'team_chat' | 'event_chat' | 'user_chat' | 'love' | 'comment', userChatId?: string) {
    try {
      const notificationsRef = collection(this.firestore, 'users', this.userId, 'notifications');

      let docPath = '';

      if (action === 'user_chat') {
        docPath = `${action}/${id}/${userChatId}`;
      }
      else if (action === 'comment') {
        docPath = `spots/${id}`;
      }
      else {
        docPath = `${action}/${id}`;
      }

      let q = null;

      if (action === 'comment') {
        q = query(
          notificationsRef,
          where('action', '==', 'comment'),
          where('docPath', '>=', `spots/${id}/`),
          where('docPath', '<', `spots/${id}/\uf8ff`)
        );
      }
      else {
        q = query(
          notificationsRef,
          where('action', '==', action),
          where('docPath', '==', docPath)
        );
      }

      const snapshot = await getDocs(q);

      for (const snap of snapshot.docs) {
        await deleteDoc(snap.ref);
      }

      // atualizar localmente
      this.notifications = this.notifications.filter(
        n => !(n.action === action && n.id === id)
      );

      this.unreadCount = this.notifications.filter(n => !n.read).length;

    } catch (error) {
      console.error('Erro ao apagar notificações do chat:', error);
    }
  }

  // apagar notificações pelo id
  async deleteNotification(notificationId: string) {
    try {
      const notificationRef = doc(this.firestore, 'users', this.userId, 'notifications', notificationId);
      await deleteDoc(notificationRef);
      this.notifications = this.notifications.filter(n => n.notificationId !== notificationId);
      this.unreadCount = this.notifications.filter(n => !n.read).length;
    }
    catch (error) {
      console.error(error);
    }
  }

  formatNotificationDate(timestamp: any): string {
    // Converte Timestamp do Firestore para Date se necessário
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);

    const day = date.getDate().toString().padStart(2, '0');
    const monthLong = date.toLocaleString('pt-PT', { month: 'long' }); // ex: "novembro"
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    const today = new Date();

    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    if (isToday) {
      return `${this.translate.instant('today')}, ${hours}h${minutes}`;
    } else {
      return `${day} ${monthLong}, ${hours}h${minutes}`;
    }
  }

  toggleNotifications() {
    this.showNotifications = !this.showNotifications;
  }

  closeNotifications() {
    this.showNotifications = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const clickedInside = (event.target as HTMLElement).closest('.notifications-wrapper');
    if (!clickedInside) {
      this.showNotifications = false;
    }
  }

  toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
  }

  navigateToHome() {
    this.router.navigate(['/home']);
  }

  navigateToProfilePersonalInfo() {
    this.router.navigate(['/profile/personal/info']);
  }

  async getUserProfileImage(userId: string): Promise<string | null> {
    const storageRef = ref(this.storage, `users/${userId}`);
    try {
      const listResult = await listAll(storageRef);
      for (const itemRef of listResult.items) {
        if (itemRef.name === 'user_photo.jpg') {
          return await getDownloadURL(itemRef);
        }
      }
      return null;
    } catch (error) {
      console.error('Erro ao obter imagem do utilizador:', error);
      return null;
    }
  }

  openLogOutPopup() {
    this.showLogoutPopup = true;
  }

  closePopup() {
    this.showLogoutPopup = false;
  }

  confirmLogout() {
    this.showLogoutPopup = false;
    this.authService.logout();
  }
}